import { useCallback, useEffect, useRef, useState } from 'react';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { useMap } from '@vis.gl/react-google-maps';
import markerPlain from '~/assets/images/marker-plain.svg';
import { Marker } from './Marker';
import { useAppDispatch, useAppSelector } from '~/state';
import { selectMapInteractionActive } from '~/state/mapData';
import {
  clearAllSelected,
  selectSelectedPractitioner,
  setSelectedClinic,
  setSelectedPractitioner,
} from '~/state/activeSelection';
import { selectSelectedClinic } from '~/state/activeSelection';
import { selectLocation } from '~/state/location';
import { Marker as MarkerType } from '@googlemaps/markerclusterer';
import { SearchResultGroupByLocation, Views } from '~/utilities';
import { useView } from '~/hooks';

interface MarkersListProps {
  mapRestriction: google.maps.MapRestriction;
  results: SearchResultGroupByLocation[];
}

export const MarkersList = ({ mapRestriction, results }: MarkersListProps) => {
  const dispatch = useAppDispatch();
  const map = useMap();
  const { handleViewChange, isMobile } = useView();
  const selectedClinic = useAppSelector(selectSelectedClinic);
  const selectedPractitioner = useAppSelector(selectSelectedPractitioner);
  const mapInteractionActive = useAppSelector(selectMapInteractionActive);
  const location = useAppSelector(selectLocation);
  const [markers, setMarkers] = useState<{
    [key: string]: MarkerType;
  }>({});
  const clustererRef = useRef<MarkerClusterer | null>(null);

  // Initialize clusterer once when map is available
  useEffect(() => {
    if (!map) return;

    clustererRef.current = new MarkerClusterer({
      map,
      renderer: {
        render: ({ count, position }) => {
          const clusterContent = document.createElement('div');
          clusterContent.innerHTML = `
          <div class="cluster">
            <img src="${markerPlain}" height={22} width={29} alt="cluster marker" class="hover-scale" />
            <div class="cluster-count">${count}</div>
          </div>
          `;
          clusterContent.style.position = 'relative';

          return new google.maps.marker.AdvancedMarkerElement({
            position,
            content: clusterContent,
          });
        },
      },
      // MPL-1442: This is a hack to prevent the map restriction from being applied when a cluster is clicked
      // and it causes the map to zoom in to the map center instead of the cluster bounds.
      // When we remove the restriction, this can be removed.
      // https://github.com/googlemaps/js-markerclusterer/issues/601
      onClusterClick: (_, cluster, map) => {
        const bounds = cluster.bounds;
        // Temporarily remove map restriction
        map.setOptions({
          restriction: null,
        });
        // Zoom to cluster bounds
        if (bounds) {
          map.fitBounds(bounds);

          // Re-apply map restriction
          map.setOptions({
            restriction: mapRestriction,
          });
        }
      },
    });

    // Cleanup
    return () => {
      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
        clustererRef.current = null;
      }
    };
  }, [map, mapRestriction]);

  // Update markers when they change
  useEffect(() => {
    if (!clustererRef.current) return;
    clustererRef.current.clearMarkers();
    clustererRef.current.addMarkers(Object.values(markers));
  }, [markers]);

  //fit and pan the map to the search results
  useEffect(() => {
    //but not when the user is interacting with the map
    if (!results.length || mapInteractionActive) return;

    const bounds = new google.maps.LatLngBounds();
    results.forEach((result) =>
      bounds.extend({
        lat: result.locationCoordinates.latitude,
        lng: result.locationCoordinates.longitude,
      })
    );
    map?.fitBounds(bounds);
    map?.panToBounds(bounds);
  }, [map, mapInteractionActive, results]);

  // Pan to searched location even when no results
  useEffect(() => {
    if (!location?.coordinates || results.length || mapInteractionActive)
      return;

    map?.panTo({
      lat: location.coordinates.lat,
      lng: location.coordinates.lng,
    });
  }, [map, location, results.length, mapInteractionActive]);

  // pans the map to the selected clinic or practitioner
  useEffect(() => {
    // Don't pan if we're on mobile (since we'll switch to list view)
    if (isMobile) {
      return;
    }

    if (selectedClinic) {
      map?.panTo({
        lat: selectedClinic.locationCoordinates.latitude,
        lng: selectedClinic.locationCoordinates.longitude,
      });
    }
    if (selectedPractitioner) {
      map?.panTo({
        lat: selectedPractitioner.locationCoordinates.latitude,
        lng: selectedPractitioner.locationCoordinates.longitude,
      });
    }
  }, [map, selectedClinic, selectedPractitioner, isMobile]);

  // this callback will effectively get passsed as ref to the markers to keep
  // tracks of markers currently on the map & adds/removes markers
  const setMarkerRef = useCallback((marker: MarkerType | null, key: string) => {
    setMarkers((markers) => {
      if ((marker && markers[key]) || (!marker && !markers[key]))
        return markers;

      if (marker) {
        return { ...markers, [key]: marker };
      } else {
        const { [key]: _, ...newMarkers } = markers;

        return newMarkers;
      }
    });
  }, []);

  const handleMarkerClick = useCallback(
    (isMultiple: boolean, result: SearchResultGroupByLocation) => {
      dispatch(clearAllSelected());

      if (isMultiple) {
        dispatch(setSelectedClinic(result));
        // On mobile, switch to List view when multi practitioner clinic marker is clicked
        if (isMobile) {
          handleViewChange(Views.List);
        }
      } else {
        dispatch(setSelectedPractitioner(result.practitioners[0]));
      }
    },
    [dispatch, handleViewChange, isMobile]
  );

  return results.map((result) => (
    <Marker
      key={result.clinicLocationGuid}
      result={result}
      anchor={markers[result.clinicLocationGuid]}
      setMarkerRef={setMarkerRef}
      isSelected={
        selectedClinic?.clinicLocationGuid === result.clinicLocationGuid ||
        selectedPractitioner?.clinicLocationGuid === result.clinicLocationGuid
      }
      handleMarkerClick={handleMarkerClick}
    />
  ));
};
